import { useCallback, useMemo, useRef, useState } from "react";
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
} from "recharts";
import { UploadIcon, FileTextIcon, XIcon } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/client/components/base/card";
import { Button } from "@/client/components/base/button";
import { Badge } from "@/client/components/base/badge";
import { Input } from "@/client/components/base/input";
import { cn } from "@/client/utils/index";

// ─── Shared analysis ────────────────────────────────────────────────────────

import {
    ACCENT_COLOR,
    NEGATIVE_COLOR,
    NEUTRAL_COLOR,
    POSITIVE_COLOR,
    DEFAULT_CHARGE_RATES,
    EMPTY_TRADE_FILTERS,
    TRADE_LOG_COLUMNS,
    applyTradeCosts,
    computeAnalysis,
    filterTrades,
    fmtDate,
    fmtDateTime,
    fmtDayTick,
    fmtDuration,
    fmtNum,
    fmtPct,
    fmtPnl,
    fmtPnlShort,
    hasActiveTradeFilters,
    parseTradeLogCsv,
    scaleTrades,
    sortTrades,
    tradeDateKey,
    correlationMatrix,
    type Analysis,
    type DailyExpectancy,
    type HistogramBin,
    type MonthlyPnL,
    type ParsedTradeLog,
    type Trade,
    type TradeCosts,
    type TradeFilters,
    type TradeLogFormat,
    type TradeSort,
    type TradeSortKey,
    type YearlyPnL,
} from "@/client/lib/trade-log-analysis";


// ─── Sub-Components ─────────────────────────────────────────────────────────

type Tone = "positive" | "negative" | "neutral";

/** Which dialect a file was read as: native output, an Algotest export, or column-name matching. */
type FormatBadgeProps = { format: TradeLogFormat };

function FormatBadge({ format }: FormatBadgeProps) {
    return (
        <span
            className="shrink-0 rounded bg-muted px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
            title={
                format === "algotest"
                    ? "Algotest export, converted to the native trade log format"
                    : format === "backtest"
                      ? "Native backtest output"
                      : "Matched by column names"
            }
        >
            {FORMAT_LABELS[format]}
        </span>
    );
}

/** A file as it was picked, before anything has tried to read it. */
type UploadedCsv = { name: string; text: string };

/** A picked file, kept with a stable id so removal is by file and not by name. */
type LoadedFile = UploadedCsv & { id: string };

/** One uploaded file: its own parse, size multiple, and analysis, kept separate from every other file. */
type FileResult = {
    file: LoadedFile;
    log: ParsedTradeLog;
    /** This file's own quantity multiple, a whole number 0 or greater. */
    multiplier: number;
    /** This file's analysis on its own trades, or null when it held none. */
    stats: Analysis | null;
};

/**
 * What a file's chip and source row display, reduced to primitives. Components are handed these
 * rather than a `FileResult`, which carries the CSV text and every trade: React's dev profiler
 * copies each component's props on render, so passing the full file set made every render clone
 * all of it.
 */
type FileSummary = {
    id: string;
    name: string;
    format: TradeLogFormat;
    tradeCount: number;
    multiplier: number;
    /** Null when the file held no trades. */
    netPnl: number | null;
    winRate: number | null;
    dailyExpectancy: DailyExpectancy | null;
};

function summarizeFile({ file, log, multiplier, stats }: FileResult): FileSummary {
    return {
        id: file.id,
        name: file.name,
        format: log.format,
        tradeCount: log.trades.length,
        multiplier,
        netPnl: stats ? stats.netPnl : null,
        winRate: stats ? stats.winRate : null,
        dailyExpectancy: stats ? stats.dailyExpectancy : null,
    };
}

function MetricCard({
    label,
    value,
    hint,
    tone = "neutral",
    className,
}: {
    label: string;
    value: string;
    hint?: string;
    tone?: Tone;
    className?: string;
}) {
    const toneClass =
        tone === "positive" ? "text-emerald-600" : tone === "negative" ? "text-red-600" : "text-foreground";
    return (
        <div className={cn("flex flex-col gap-1 rounded-lg border bg-card p-3", className)}>
            <span className="text-xs text-muted-foreground">{label}</span>
            <span className={cn("text-lg font-semibold tabular-nums", toneClass)}>{value}</span>
            {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
        </div>
    );
}

/** Reads picked files down to name + text: everything downstream is pure over that text. */
const readFiles = (picked: File[]): Promise<UploadedCsv[]> =>
    Promise.all(picked.map(async (file) => ({ name: file.name, text: await file.text() })));

function UploadZone({ onFiles }: { onFiles: (files: UploadedCsv[]) => void }) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = useState(false);

    const handleFiles = useCallback(
        async (picked: ArrayLike<File> | null | undefined) => {
            if (!picked || picked.length === 0) return;
            onFiles(await readFiles(Array.from(picked)));
        },
        [onFiles],
    );

    const onDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setDragging(false);
            void handleFiles(e.dataTransfer.files);
        },
        [handleFiles],
    );

    return (
        <div
            className={cn(
                "flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-16 text-center transition-colors",
                dragging ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/50",
            )}
            onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
        >
            <UploadIcon className="mb-4 size-10 text-muted-foreground/50" />
            <p className="text-sm font-medium text-foreground">Drop your trade log CSVs here</p>
            <p className="mt-1 text-xs text-muted-foreground">
                one or many — they are analysed together · or click to browse
            </p>
            <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => inputRef.current?.click()}
            >
                <FileTextIcon data-icon="inline-start" />
                Choose CSV files
            </Button>
            <input
                ref={inputRef}
                type="file"
                accept=".csv"
                multiple
                className="hidden"
                onChange={(e) => {
                    // Captured before the value is cleared, so re-picking the same file fires again.
                    const picked = e.target.files ? Array.from(e.target.files) : [];
                    e.target.value = "";
                    void handleFiles(picked);
                }}
            />
            <div className="mt-6 max-w-md text-left text-xs text-muted-foreground">
                <p className="mb-1 font-medium">Two formats accepted:</p>
                <p className="mb-1">
                    <span className="font-medium text-foreground">Native backtest output</span> (from data/results):{" "}
                    <span className="font-mono">{TRADE_LOG_COLUMNS.join(", ")}</span>
                </p>
                <p>
                    <span className="font-medium text-foreground">Algotest exports</span> — recognised by their
                    headers and converted automatically, one trade per option leg. TradingView exports are matched by
                    column name too.
                </p>
                <p className="mt-2">
                    <code>Side</code> is long or short, <code>P/L</code> is net of <code>Fees</code>, and rows with a
                    blank exit are counted as open.
                </p>
            </div>
        </div>
    );
}

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: { value: number; name: string; color: string }[]; label?: string }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-sm">
            <p className="font-medium text-foreground">{label}</p>
            {payload.map((p, i) => (
                <p key={i} style={{ color: p.color }}>
                    {p.name}: {fmtPnl(p.value)}
                </p>
            ))}
        </div>
    );
}

/** One loaded file: its name, its own size multiple, what it contributed, and a way to take it back out. */
function FileChip({
    summary,
    multiplier,
    onMultiplierChange,
    onMultiplierBlur,
    onRemove,
}: {
    summary: FileSummary;
    /** Raw text of this file's multiple, sanitised to whole numbers. */
    multiplier: string;
    onMultiplierChange: (raw: string) => void;
    onMultiplierBlur: () => void;
    onRemove: () => void;
}) {
    const { name, format, tradeCount, netPnl } = summary;
    return (
        <span className="inline-flex max-w-full items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-xs">
            <FileTextIcon className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate font-medium text-foreground" title={name}>
                {name}
            </span>
            <FormatBadge format={format} />
            <span className="shrink-0 text-muted-foreground">
                {tradeCount} {tradeCount === 1 ? "trade" : "trades"}
                {netPnl !== null && ` · ${fmtPnl(netPnl)}`}
            </span>
            <label
                className="flex shrink-0 items-center gap-1 border-l pl-2 text-muted-foreground"
                title={`Quantity multiplier for ${name}`}
            >
                <span>×</span>
                <Input
                    type="number"
                    min={0}
                    step={1}
                    inputMode="numeric"
                    value={multiplier}
                    onChange={(e) => onMultiplierChange(e.target.value)}
                    onBlur={onMultiplierBlur}
                    aria-label={`Quantity multiplier for ${name}`}
                    className="h-6 w-14 rounded px-1 text-right text-xs tabular-nums"
                />
            </label>
            <button
                type="button"
                onClick={onRemove}
                aria-label={`Remove ${name}`}
                className="shrink-0 rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
                <XIcon className="size-3" />
            </button>
        </span>
    );
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const SELECT_CLASS =
    "h-8 w-full min-w-0 rounded-lg border border-input bg-transparent px-2 text-sm outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 dark:bg-input/30";

/** Narrows the trades every figure on the page is built from. */
function TradeFilterBar({
    filters,
    onChange,
    matched,
    total,
}: {
    filters: TradeFilters;
    onChange: (next: TradeFilters) => void;
    matched: number;
    total: number;
}) {
    const update = (patch: Partial<TradeFilters>) => onChange({ ...filters, ...patch });
    const field = (label: string, control: React.ReactNode) => (
        <label className="flex min-w-0 flex-col gap-1 text-xs text-muted-foreground">
            {label}
            {control}
        </label>
    );
    const numberField = (key: "pnlMin" | "pnlMax" | "pnlPctMin" | "pnlPctMax", label: string) =>
        field(
            label,
            <Input
                type="number"
                step="any"
                inputMode="decimal"
                placeholder="any"
                value={filters[key]}
                onChange={(e) => update({ [key]: e.target.value } as Partial<TradeFilters>)}
            />,
        );
    const dateField = (key: "entryFrom" | "entryTo", label: string) =>
        field(
            label,
            <Input
                type="date"
                value={filters[key]}
                onChange={(e) => update({ [key]: e.target.value } as Partial<TradeFilters>)}
            />,
        );
    const active = hasActiveTradeFilters(filters);

    return (
        <div className="flex flex-col gap-3 rounded-lg border bg-muted/40 px-3 py-3">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="text-sm font-medium">Filter trades</span>
                    <span className="text-xs text-muted-foreground">
                        Showing {matched} of {total} trades · P&L and return bounds apply to closed trades only
                    </span>
                </div>
                <Button variant="outline" size="sm" disabled={!active} onClick={() => onChange(EMPTY_TRADE_FILTERS)}>
                    <XIcon data-icon="inline-start" />
                    Clear filters
                </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
                {field(
                    "Symbol",
                    <Input
                        type="text"
                        placeholder="contains…"
                        value={filters.symbol}
                        onChange={(e) => update({ symbol: e.target.value })}
                    />,
                )}
                {field(
                    "Side",
                    <select
                        className={SELECT_CLASS}
                        value={filters.side}
                        onChange={(e) => update({ side: e.target.value as TradeFilters["side"] })}
                    >
                        <option value="all">All</option>
                        <option value="long">Long</option>
                        <option value="short">Short</option>
                    </select>,
                )}
                {field(
                    "Status",
                    <select
                        className={SELECT_CLASS}
                        value={filters.status}
                        onChange={(e) => update({ status: e.target.value as TradeFilters["status"] })}
                    >
                        <option value="all">All</option>
                        <option value="closed">Closed</option>
                        <option value="open">Open</option>
                    </select>,
                )}
                {numberField("pnlMin", "Net P&L min (₹)")}
                {numberField("pnlMax", "Net P&L max (₹)")}
                {numberField("pnlPctMin", "P&L % min")}
                {numberField("pnlPctMax", "P&L % max")}
                {dateField("entryFrom", "Entry from")}
                {dateField("entryTo", "Entry to")}
            </div>
        </div>
    );
}

// ─── Main Component ─────────────────────────────────────────────────────────

type Tab = "overview" | "breakdown" | "trades";

const TABS: { key: Tab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "breakdown", label: "Breakdown" },
    { key: "trades", label: "Trades" },
];

/** Session-local id source: `crypto.randomUUID` is unavailable on an insecure origin, and these ids only ever live in React state. */
let fileIdSequence = 0;

const EMPTY_LOG: ParsedTradeLog = { trades: [], headers: [], pnlIsNetOfFees: false, skippedRows: 0, format: "generic" };

/** Short label for the dialect a file was read as, shown on its chip and in the source table. */
const FORMAT_LABELS: Record<ParsedTradeLog["format"], string> = {
    backtest: "native",
    algotest: "algotest",
    generic: "csv",
};

/**
 * A per-strategy size multiple: always a whole number, 0 or greater. A blank or malformed
 * field reads as 1, so the numbers stay meaningful mid-edit.
 */
function parseMultiplier(raw: string | undefined): number {
    const digits = (raw ?? "").replace(/[^0-9]/g, "");
    return digits === "" ? 1 : Number.parseInt(digits, 10);
}

/** A cost input's raw text as a non-negative number; blank or malformed reads as 0. */
function parseCostInput(raw: string): number {
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Keeps a cost field non-negative while it is being typed. */
function sanitizeCostInput(raw: string): string {
    if (raw.trim() === "") return "";
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) && n >= 0 ? raw : "0";
}

/** One switch and its value: a cost applies only while its box is ticked. */
function CostToggle({
    label,
    checked,
    onCheckedChange,
    value,
    onValueChange,
    suffix,
    hint,
}: {
    label: string;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    value: string;
    onValueChange: (raw: string) => void;
    suffix: string;
    hint: string;
}) {
    return (
        <div className="flex flex-col gap-1 rounded-md border bg-card px-2.5 py-2">
            <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-foreground">
                <input
                    type="checkbox"
                    className="size-3.5 accent-primary"
                    checked={checked}
                    onChange={(e) => onCheckedChange(e.target.checked)}
                />
                {label}
            </label>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Input
                    type="number"
                    min={0}
                    step="any"
                    inputMode="decimal"
                    value={value}
                    disabled={!checked}
                    onChange={(e) => onValueChange(sanitizeCostInput(e.target.value))}
                    aria-label={`${label} amount`}
                    className="h-6 w-20 rounded px-1 text-right text-xs tabular-nums"
                />
                <span>{suffix}</span>
                <span className="hidden sm:inline">· {hint}</span>
            </div>
        </div>
    );
}

/** Entry time, falling back to the exit for a row that has none, so merged files stay in order. */
const entryTimestamp = (trade: Trade): number => {
    const entry = trade.entryDate.getTime();
    if (!Number.isNaN(entry)) return entry;
    const exit = trade.exitDate.getTime();
    return Number.isNaN(exit) ? 0 : exit;
};

export default function TradeLogAnalysisPage() {
    const [files, setFiles] = useState<LoadedFile[]>([]);
    const [activeTab, setActiveTab] = useState<Tab>("overview");
    /** null keeps each file's own detection, which knows a backtest log's P/L is already net. */
    const [pnlIsNetOverride, setPnlIsNetOverride] = useState<boolean | null>(null);
    /**
     * Position-size multiple per strategy, keyed by file name so a re-uploaded log keeps its
     * setting. Held as raw text so a field can be cleared mid-edit and is sanitised to a whole
     * number everywhere it is read.
     */
    const [multiplierInputs, setMultiplierInputs] = useState<Record<string, string>>({});
    const setMultiplierInput = useCallback((name: string, raw: string) => {
        setMultiplierInputs((prev) => {
            // A whole number, 0 or greater: negatives clamp to 0, decimals keep their integer part.
            if (raw.trim().startsWith("-")) return { ...prev, [name]: "0" };
            const [whole = ""] = raw.split(".");
            return { ...prev, [name]: whole.replace(/[^0-9]/g, "") };
        });
    }, []);
    const normalizeMultiplierInput = useCallback((name: string) => {
        setMultiplierInputs((prev) => ({ ...prev, [name]: String(parseMultiplier(prev[name])) }));
    }, []);
    /** The sanitised multiple for each loaded file: a whole number, 0 or greater. */
    const multipliers = useMemo(() => {
        const out: Record<string, number> = {};
        for (const file of files) out[file.name] = parseMultiplier(multiplierInputs[file.name]);
        return out;
    }, [files, multiplierInputs]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    /** Taxes and charges, slippage and brokerage: each is applied only while its box is ticked. */
    const [applyCharges, setApplyCharges] = useState(false);
    const [applySlippage, setApplySlippage] = useState(false);
    const [slippageInput, setSlippageInput] = useState("0");
    const [applyBrokerage, setApplyBrokerage] = useState(false);
    const [brokerageInput, setBrokerageInput] = useState("0");
    /** Narrows the Trades tab only; the stats on every other tab always cover the whole log. */
    const [filters, setFilters] = useState<TradeFilters>(EMPTY_TRADE_FILTERS);
    const [sort, setSort] = useState<TradeSort>({ key: "entryTime", dir: "asc" });
    const [showCosts, setShowCosts] = useState(false);
    const tradeCosts = useMemo<TradeCosts>(
        () => ({
            charges: applyCharges,
            slippagePct: applySlippage ? parseCostInput(slippageInput) : 0,
            brokeragePerOrder: applyBrokerage ? parseCostInput(brokerageInput) : 0,
        }),
        [applyCharges, applySlippage, slippageInput, applyBrokerage, brokerageInput],
    );

    /**
     * A file picked again replaces the one already loaded under that name rather than joining it:
     * re-dragging a regenerated backtest log refreshes it instead of silently double counting,
     * and the loaded set stays one chip per file name.
     */
    const addFiles = useCallback((picked: UploadedCsv[]) => {
        setFiles((prev) => [
            ...prev.filter((file) => !picked.some((upload) => upload.name === file.name)),
            ...picked.map((upload) => ({ ...upload, id: `file-${++fileIdSequence}` })),
        ]);
    }, []);

    const removeFile = useCallback((id: string) => {
        setFiles((prev) => prev.filter((file) => file.id !== id));
    }, []);

    /** Files dragged over the loaded page, so new logs can be dropped at any point during analysis. */
    const [pageDragging, setPageDragging] = useState(false);
    /** Drag enter/leave fire for every child element, so the count keeps the overlay steady. */
    const dragDepth = useRef(0);
    const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes("Files");
    const onPageDragEnter = (e: React.DragEvent) => {
        if (!isFileDrag(e)) return;
        e.preventDefault();
        dragDepth.current++;
        setPageDragging(true);
    };
    const onPageDragLeave = (e: React.DragEvent) => {
        if (!isFileDrag(e)) return;
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setPageDragging(false);
    };
    const onPageDragOver = (e: React.DragEvent) => {
        if (!isFileDrag(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
    };
    const onPageDrop = (e: React.DragEvent) => {
        if (!isFileDrag(e)) return;
        e.preventDefault();
        dragDepth.current = 0;
        setPageDragging(false);
        const picked = Array.from(e.dataTransfer.files);
        if (picked.length > 0) void readFiles(picked).then(addFiles);
    };

    const clearFiles = useCallback(() => {
        setFiles([]);
        setPnlIsNetOverride(null);
        setMultiplierInputs({});
        setFilters(EMPTY_TRADE_FILTERS);
    }, []);

    /** A loaded file, with its own size multiple wired to the input inside its chip. */
    const renderFileChip = (summary: FileSummary) => (
        <FileChip
            key={summary.id}
            summary={summary}
            multiplier={multiplierInputs[summary.name] ?? "1"}
            onMultiplierChange={(raw) => setMultiplierInput(summary.name, raw)}
            onMultiplierBlur={() => normalizeMultiplierInput(summary.name)}
            onRemove={() => removeFile(summary.id)}
        />
    );

    /** Parsed and analysed per file, so a bad file is reported under its own name and never poisons the rest. */
    const fileResults = useMemo<FileResult[]>(
        () =>
            files.map((file) => {
                let log: ParsedTradeLog;
                try {
                    log = parseTradeLogCsv(file.text, { pnlIsNetOfFees: pnlIsNetOverride ?? undefined });
                } catch (err) {
                    log = { ...EMPTY_LOG, error: err instanceof Error ? err.message : "Failed to parse CSV" };
                }
                // Each file is scaled by its own multiple, so every number on the page — including
                // each strategy's contribution — reflects its chosen position size.
                // Costs come after scaling, so brokerage stays per order while fees and slippage follow the size.
                const multiplier = multipliers[file.name] ?? 1;
                if (log.trades.length > 0) {
                    const scaled = scaleTrades(log.trades, multiplier);
                    log = { ...log, trades: applyTradeCosts(scaled, tradeCosts, log.pnlIsNetOfFees) };
                }
                return {
                    file,
                    log,
                    multiplier,
                    stats: log.trades.length > 0 ? computeAnalysis(log.trades) : null,
                };
            }),
        [files, pnlIsNetOverride, multipliers, tradeCosts],
    );

    const fileSummaries = useMemo(() => fileResults.map(summarizeFile), [fileResults]);

    /** Daily net P&L correlation between every strategy that holds trades. */
    const correlation = useMemo<Correlation>(() => {
        const withTrades = fileResults.filter((result) => result.stats);
        return {
            names: withTrades.map((result) => result.file.name),
            matrix: correlationMatrix(withTrades.map((result) => result.stats!.dailyPnl)),
        };
    }, [fileResults]);

    const log = useMemo<ParsedTradeLog | null>(() => {
        if (fileResults.length === 0) return null;
        // A blend of dialects is still parseable, so the combined header only claims one
        // when every file agreed on it.
        const formats = new Set(fileResults.map((result) => result.log.format));
        return {
            trades: fileResults.flatMap((result) => result.log.trades).sort((a, b) => entryTimestamp(a) - entryTimestamp(b)),
            headers: [...new Set(fileResults.flatMap((result) => result.log.headers))],
            pnlIsNetOfFees: pnlIsNetOverride ?? fileResults.every((result) => result.log.pnlIsNetOfFees),
            skippedRows: fileResults.reduce((sum, result) => sum + result.log.skippedRows, 0),
            format: formats.size === 1 ? [...formats][0]! : "generic",
        };
    }, [fileResults, pnlIsNetOverride]);

    const analysis = useMemo<Analysis | null>(() => {
        if (!log || log.trades.length === 0) return null;
        return computeAnalysis(log.trades);
    }, [log]);

    /** One notice per file that needs attention or a word of explanation, named by file. */
    type Notice = { message: string; tone: "error" };
    const notices: Notice[] = fileResults.flatMap(({ file, log }): Notice[] => {
        if (log.error) return [{ message: `${file.name}: ${log.error}`, tone: "error" as const }];
        if (log.trades.length === 0)
            return [
                {
                    message: `${file.name}: no trades found. Expected columns: ${TRADE_LOG_COLUMNS.join(", ")} — or an Algotest export`,
                    tone: "error" as const,
                },
            ];
        if (log.skippedRows > 0)
            return [
                {
                    message: `${file.name}: ${log.skippedRows} row(s) skipped: no usable EntryTime or ExitTime.`,
                    tone: "error" as const,
                },
            ];
        return [];
    });

    const renderNotice = (notice: (typeof notices)[number]) => (
        <div
            key={notice.message}
            className="rounded-lg border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive"
        >
            {notice.message}
        </div>
    );

    if (files.length === 0 || !analysis || !log) {
        return (
            <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6">
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold tracking-tight">Trade Log Analysis</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Upload backtest trade logs — native or Algotest exports — to get combined trade analytics
                        across all of them.
                    </p>
                </div>
                {notices.length > 0 && <div className="mb-4 flex flex-col gap-2">{notices.map(renderNotice)}</div>}
                {files.length > 0 && (
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                        <span className="text-xs text-muted-foreground">Loaded:</span>
                        {fileSummaries.map(renderFileChip)}
                    </div>
                )}
                <UploadZone onFiles={addFiles} />
            </div>
        );
    }

    const firstTrade = analysis.trades[0];
    const lastTrade = analysis.trades[analysis.trades.length - 1];

    return (
        <div
            className="relative mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6"
            onDragEnter={onPageDragEnter}
            onDragLeave={onPageDragLeave}
            onDragOver={onPageDragOver}
            onDrop={onPageDrop}
        >
            {pageDragging && (
                <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-primary bg-primary/5 backdrop-blur-[1px]">
                    <p className="rounded-md bg-background px-3 py-1.5 text-sm font-medium text-foreground shadow-sm">
                        Drop to add trade logs
                    </p>
                </div>
            )}

            {/* Per-strategy position size: each CSV carries its own whole-number multiple, the
                first control on the page so every figure below can be re-read at a new size. */}
            <div className="flex flex-col gap-2 rounded-lg border bg-muted/40 px-3 py-2">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <span className="text-sm font-medium">Position size per strategy</span>
                    <span className="text-xs text-muted-foreground">
                        Whole numbers only (0 or more). ×0 excludes that file; ×1 keeps it as logged.
                    </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">{fileSummaries.map(renderFileChip)}</div>
            </div>

            {/* Costs applied on top of the logged P/L: taxes and charges, slippage, brokerage per order */}
            <div className="flex flex-col gap-2 rounded-lg border bg-muted/40 px-3 py-2">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <span className="text-sm font-medium">Costs</span>
                    <span className="text-xs text-muted-foreground">
                        Closed positions pay two orders (entry and exit), open positions one (entry).
                    </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <label className="flex cursor-pointer items-center gap-1.5 rounded-md border bg-card px-2.5 py-2 text-xs font-medium">
                        <input
                            type="checkbox"
                            className="size-3.5 accent-primary"
                            checked={applyCharges}
                            onChange={(e) => setApplyCharges(e.target.checked)}
                        />
                        Apply taxes and charges
                        <span className="font-normal text-muted-foreground">
                            · % of value: options {DEFAULT_CHARGE_RATES.options.buy}/{DEFAULT_CHARGE_RATES.options.sell}, futures{" "}
                            {DEFAULT_CHARGE_RATES.futures.buy}/{DEFAULT_CHARGE_RATES.futures.sell} (buy/sell)
                        </span>
                    </label>
                    <CostToggle
                        label="Slippage"
                        checked={applySlippage}
                        onCheckedChange={setApplySlippage}
                        value={slippageInput}
                        onValueChange={setSlippageInput}
                        suffix="% per order"
                        hint="of traded value"
                    />
                    <CostToggle
                        label="Brokerage"
                        checked={applyBrokerage}
                        onCheckedChange={setApplyBrokerage}
                        value={brokerageInput}
                        onValueChange={setBrokerageInput}
                        suffix="per order"
                        hint="fixed amount"
                    />
                </div>
            </div>

            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-lg font-semibold">Trade Log Analysis</h1>
                    <p className="text-sm text-muted-foreground">
                        {files.length} {files.length === 1 ? "file" : "files"} · {analysis.closedTrades} closed
                        {analysis.openTrades > 0 && ` · ${analysis.openTrades} open`} trades ·{" "}
                        {fmtDate(tradeDateKey(firstTrade?.entryDate ?? new Date(NaN), "-"))}
                        {" → "}
                        {fmtDate(tradeDateKey(lastTrade?.exitDate ?? lastTrade?.entryDate ?? new Date(NaN), "-"))}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                        <input
                            type="checkbox"
                            className="size-3.5 accent-primary"
                            checked={log.pnlIsNetOfFees}
                            onChange={(e) => setPnlIsNetOverride(e.target.checked)}
                        />
                        P/L is net of fees
                    </label>
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                        <UploadIcon data-icon="inline-start" />
                        Add files
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                            // Captured before the value is cleared, so re-picking the same file fires again.
                            const picked = e.target.files ? Array.from(e.target.files) : [];
                            e.target.value = "";
                            if (picked.length > 0) void readFiles(picked).then(addFiles);
                        }}
                    />
                    <Button variant="outline" size="sm" onClick={clearFiles}>
                        <XIcon data-icon="inline-start" />
                        Clear all
                    </Button>
                </div>
            </div>

            {notices.map(renderNotice)}

            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
                <MetricCard
                    label="Net P&L"
                    value={fmtPnl(analysis.netPnl)}
                    tone={analysis.netPnl >= 0 ? "positive" : "negative"}
                />
                <MetricCard label="Total Trades" value={String(analysis.totalTrades)} />
                <MetricCard
                    label="Win Rate"
                    value={`${analysis.winRate.toFixed(1)}%`}
                    hint={`${analysis.winningTrades}W / ${analysis.losingTrades}L`}
                    tone={analysis.winRate >= 50 ? "positive" : "negative"}
                />
                <MetricCard
                    label="Profit Factor"
                    value={Number.isFinite(analysis.profitFactor) ? fmtNum(analysis.profitFactor) : "∞"}
                    tone={analysis.profitFactor >= 1.5 ? "positive" : analysis.profitFactor >= 1 ? "neutral" : "negative"}
                />
                <MetricCard
                    label="Expectancy Ratio"
                    value={Number.isFinite(analysis.expectancyRatio) ? fmtNum(analysis.expectancyRatio, 2) : "∞"}
                    hint="Win rate × payoff − loss rate"
                    tone={analysis.expectancyRatio >= 0 ? "positive" : "negative"}
                />
                <MetricCard
                    label="Return / Max DD per Year"
                    value={Number.isFinite(analysis.avgReturnToMaxDrawdown) ? fmtNum(analysis.avgReturnToMaxDrawdown, 2) : "∞"}
                    hint={`Net P&L ÷ Max DD ÷ ${fmtNum(analysis.tradingYears, 2)} yrs`}
                    tone={analysis.avgReturnToMaxDrawdown >= 0 ? "positive" : "negative"}
                />
            </div>

            {/* Tabs */}
            <div className="flex gap-0.5 border-b">
                {TABS.map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={cn(
                            "relative px-3 py-2 text-sm font-medium transition-colors",
                            activeTab === tab.key
                                ? "text-foreground"
                                : "text-muted-foreground hover:text-foreground",
                        )}
                    >
                        {tab.label}
                        {activeTab === tab.key && (
                            <span className="absolute inset-x-0 -bottom-px h-0.5 bg-primary" />
                        )}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeTab === "overview" && (
                <OverviewTab analysis={analysis} files={fileSummaries} correlation={correlation} />
            )}
            {activeTab === "breakdown" && <BreakdownTab analysis={analysis} />}
            {activeTab === "trades" && (
                <TradesTab
                    analysis={analysis}
                    filters={filters}
                    onFiltersChange={setFilters}
                    sort={sort}
                    onSortChange={setSort}
                    showCosts={showCosts}
                    onShowCostsChange={setShowCosts}
                />
            )}
        </div>
    );
}

// ─── Overview Tab ───────────────────────────────────────────────────────────

/**
 * Every file the combined numbers are built from, with what each one contributed on its own.
 * A row per file rather than a name in the corner: with several logs merged, "which of these
 * is dragging the total down" is the first question the page has to answer.
 */
function SourceFilesCard({ files }: { files: FileSummary[] }) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Source Files</CardTitle>
                <CardDescription>
                    {files.length} {files.length === 1 ? "file" : "files"} combined into the results below, each at
                    its own size multiple
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b text-left text-xs text-muted-foreground">
                                <th className="pb-2 pr-4 font-medium">File</th>
                                <th className="pb-2 pr-4 font-medium">Format</th>
                                <th className="pb-2 pr-4 text-right font-medium">Qty ×</th>
                                <th className="pb-2 pr-4 text-right font-medium">Trades</th>
                                <th className="pb-2 pr-4 text-right font-medium">Win Rate</th>
                                <th className="pb-2 text-right font-medium">Net P&L</th>
                            </tr>
                        </thead>
                        <tbody>
                            {files.map((file) => (
                                <tr key={file.id} className="border-b last:border-0">
                                    <td className="max-w-xs py-1.5 pr-4">
                                        <span className="inline-flex items-center gap-1.5">
                                            <FileTextIcon className="size-3.5 shrink-0 text-muted-foreground" />
                                            <span className="truncate font-medium" title={file.name}>
                                                {file.name}
                                            </span>
                                        </span>
                                    </td>
                                    <td className="py-1.5 pr-4">
                                        <FormatBadge format={file.format} />
                                    </td>
                                    <td className="py-1.5 pr-4 text-right tabular-nums">×{file.multiplier}</td>
                                    <td className="py-1.5 pr-4 text-right tabular-nums">{file.tradeCount}</td>
                                    <td className="py-1.5 pr-4 text-right tabular-nums">
                                        {file.winRate !== null ? `${file.winRate.toFixed(1)}%` : "-"}
                                    </td>
                                    <td
                                        className={cn(
                                            "py-1.5 text-right font-medium tabular-nums",
                                            file.netPnl === null
                                                ? "text-muted-foreground"
                                                : file.netPnl >= 0
                                                  ? "text-emerald-600"
                                                  : "text-red-600",
                                        )}
                                    >
                                        {file.netPnl !== null ? fmtPnl(file.netPnl) : "-"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </CardContent>
        </Card>
    );
}

/** Strategies' daily net P&L correlation: names in the matrix's row and column order. */
type Correlation = { names: string[]; matrix: number[][] };

function DailyExpectancyCard({ analysis, files }: { analysis: Analysis; files: FileSummary[] }) {
    const rows: { key: string; label: string; stats: DailyExpectancy; bold: boolean }[] = [
        { key: "combined", label: "Combined", stats: analysis.dailyExpectancy, bold: true },
        ...(files.length > 1
            ? files.flatMap((file) =>
                  file.dailyExpectancy
                      ? [{ key: file.id, label: file.name, stats: file.dailyExpectancy, bold: false }]
                      : [],
              )
            : []),
    ];
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Expectancy per Day</CardTitle>
                <CardDescription>
                    Each trading day counts as one outcome. Day win rate × avg winning day − day loss rate × avg
                    losing day. Compare strategies here to see how their typical days pay off.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b text-left text-xs text-muted-foreground">
                                <th className="pb-2 pr-4 font-medium">Strategy</th>
                                <th className="pb-2 pr-4 text-right font-medium">Days</th>
                                <th className="pb-2 pr-4 text-right font-medium">Win / Loss Days</th>
                                <th className="pb-2 pr-4 text-right font-medium">Day Win Rate</th>
                                <th className="pb-2 pr-4 text-right font-medium">Avg Winning Day</th>
                                <th className="pb-2 pr-4 text-right font-medium">Avg Losing Day</th>
                                <th className="pb-2 pr-4 text-right font-medium">Expectancy Ratio</th>
                                <th className="pb-2 text-right font-medium">Expectancy / Day</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.key} className={cn("border-b last:border-0", row.bold && "font-medium")}>
                                    <td className="max-w-xs truncate py-1.5 pr-4" title={row.label}>
                                        {row.label}
                                    </td>
                                    <td className="py-1.5 pr-4 text-right tabular-nums">{row.stats.days}</td>
                                    <td className="py-1.5 pr-4 text-right tabular-nums">
                                        {row.stats.winningDays} / {row.stats.losingDays}
                                    </td>
                                    <td className="py-1.5 pr-4 text-right tabular-nums">{row.stats.dayWinRate.toFixed(1)}%</td>
                                    <td className="py-1.5 pr-4 text-right tabular-nums text-emerald-600">
                                        {fmtPnl(row.stats.avgWinningDay)}
                                    </td>
                                    <td className="py-1.5 pr-4 text-right tabular-nums text-red-600">
                                        {fmtPnl(-row.stats.avgLosingDay)}
                                    </td>
                                    <td
                                        className={cn(
                                            "py-1.5 pr-4 text-right tabular-nums",
                                            row.stats.expectancyRatio >= 0 ? "text-emerald-600" : "text-red-600",
                                        )}
                                    >
                                        {Number.isFinite(row.stats.expectancyRatio) ? fmtNum(row.stats.expectancyRatio, 2) : "∞"}
                                    </td>
                                    <td
                                        className={cn(
                                            "py-1.5 text-right tabular-nums",
                                            row.stats.expectancy >= 0 ? "text-emerald-600" : "text-red-600",
                                        )}
                                    >
                                        {fmtPnl(row.stats.expectancy)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </CardContent>
        </Card>
    );
}

function CorrelationCard({ correlation }: { correlation: Correlation }) {
    const { names, matrix } = correlation;
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Daily Return Correlation</CardTitle>
                <CardDescription>
                    Pearson correlation of each strategy's daily net P&L. Days a strategy did not trade count as 0.
                    Values near 0 mean the strategies move independently.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {names.length < 2 ? (
                    <p className="text-sm text-muted-foreground">
                        Load two or more strategies to see how their daily returns move together.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs text-muted-foreground">
                                    <th className="pb-2 pr-4 text-left font-medium" />
                                    {names.map((name) => (
                                        <th key={name} className="max-w-[10rem] truncate pb-2 pr-2 text-center font-medium" title={name}>
                                            {name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {matrix.map((row, i) => (
                                    <tr key={names[i]} className="border-t">
                                        <td className="max-w-[10rem] truncate py-1.5 pr-4 text-xs font-medium" title={names[i]}>
                                            {names[i]}
                                        </td>
                                        {row.map((r, j) => (
                                            <td
                                                key={j}
                                                className="px-2 py-1.5 text-center tabular-nums"
                                                style={{ backgroundColor: correlationFill(r) }}
                                            >
                                                {Number.isFinite(r) ? r.toFixed(2) : "-"}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

/** Green for positive correlation, red for negative, fading to clear as it nears zero. */
function correlationFill(r: number): string | undefined {
    if (!Number.isFinite(r)) return undefined;
    const alpha = (Math.min(1, Math.abs(r)) * 0.35).toFixed(3);
    return r >= 0 ? `rgba(16, 185, 129, ${alpha})` : `rgba(239, 68, 68, ${alpha})`;
}

function OverviewTab({
    analysis,
    files,
    correlation,
}: {
    analysis: Analysis;
    files: FileSummary[];
    correlation: Correlation;
}) {
    return (
        <div className="flex flex-col gap-4">
            {/* Which files these numbers came from, and each one's own contribution */}
            <SourceFilesCard files={files} />

            {/* Equity Curve */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Equity Curve</CardTitle>
                    <CardDescription>Cumulative P&L over time</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="h-72 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={analysis.equityCurve} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="eqGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={ACCENT_COLOR} stopOpacity={0.3} />
                                        <stop offset="100%" stopColor={ACCENT_COLOR} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={(v: string) => {
                                        return fmtDayTick(v);
                                    }}
                                    tickLine={false}
                                    axisLine={false}
                                    interval="preserveStartEnd"
                                    minTickGap={60}
                                />
                                <YAxis
                                    tickFormatter={(v: number) => fmtPnlShort(v)}
                                    tickLine={false}
                                    axisLine={false}
                                    width={60}
                                />
                                <Tooltip content={<ChartTooltip />} />
                                <ReferenceLine y={0} stroke="#64748b" strokeDasharray="3 3" />
                                <Area
                                    type="monotone"
                                    dataKey="equity"
                                    name="Equity"
                                    stroke={ACCENT_COLOR}
                                    fill="url(#eqGradient)"
                                    strokeWidth={2}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Drawdown Chart */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Drawdown</CardTitle>
                    <CardDescription>Drawdown from equity peak</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="h-48 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={analysis.equityCurve} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="ddGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={NEGATIVE_COLOR} stopOpacity={0} />
                                        <stop offset="100%" stopColor={NEGATIVE_COLOR} stopOpacity={0.3} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={(v: string) => {
                                        return fmtDayTick(v);
                                    }}
                                    tickLine={false}
                                    axisLine={false}
                                    interval="preserveStartEnd"
                                    minTickGap={60}
                                />
                                <YAxis
                                    tickFormatter={(v: number) => fmtPnlShort(v)}
                                    tickLine={false}
                                    axisLine={false}
                                    width={60}
                                />
                                <Tooltip content={<ChartTooltip />} />
                                <Area
                                    type="monotone"
                                    dataKey="drawdown"
                                    name="Drawdown"
                                    stroke={NEGATIVE_COLOR}
                                    fill="url(#ddGradient)"
                                    strokeWidth={1.5}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Win/Loss Analysis */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Win/Loss Analysis</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 gap-3">
                        <MetricCard label="Winning Trades" value={String(analysis.winningTrades)} tone="positive" />
                        <MetricCard label="Losing Trades" value={String(analysis.losingTrades)} tone="negative" />
                        <MetricCard
                            label="Breakeven Trades"
                            value={String(analysis.breakevenTrades)}
                        />
                        <MetricCard
                            label="Loss Rate"
                            value={`${analysis.lossRate.toFixed(1)}%`}
                            tone="negative"
                        />
                        <MetricCard label="Avg Winner" value={fmtPnl(analysis.avgWin)} tone="positive" />
                        <MetricCard label="Avg Loser" value={fmtPnl(-analysis.avgLoss)} tone="negative" />
                        <MetricCard label="Median Winner" value={fmtPnl(analysis.medianWin)} tone="positive" />
                        <MetricCard label="Median Loser" value={fmtPnl(analysis.medianLoss)} tone="negative" />
                        <MetricCard label="Largest Winner" value={fmtPnl(analysis.largestWin)} tone="positive" />
                        <MetricCard label="Largest Loser" value={fmtPnl(analysis.largestLoss)} tone="negative" />
                        <MetricCard
                            label="Payoff Ratio"
                            value={Number.isFinite(analysis.payoffRatio) ? fmtNum(analysis.payoffRatio) : "∞"}
                            hint="Avg Win / Avg Loss"
                        />
                        <MetricCard label="Gross Profit" value={fmtPnl(analysis.grossProfit)} tone="positive" />
                        <MetricCard label="Gross Loss" value={fmtPnl(-analysis.grossLoss)} tone="negative" />
                        <MetricCard label="Total Fees" value={fmtPnl(analysis.totalFees)} hint="Taxes & charges" />
                        <MetricCard label="Total Brokerage" value={fmtPnl(analysis.totalBrokerage)} />
                        <MetricCard label="Total Slippage" value={fmtPnl(analysis.totalSlippage)} />
                        <MetricCard label="Total Costs" value={fmtPnl(analysis.totalCosts)} />
                    </CardContent>
                </Card>

                {/* Risk & Time Metrics */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Risk & Performance</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 gap-3">
                        <MetricCard
                            label="Sharpe Ratio"
                            value={fmtNum(analysis.sharpeRatio)}
                            hint="Annualised (simplified)"
                        />
                        <MetricCard
                            label="Sortino Ratio"
                            value={fmtNum(analysis.sortinoRatio)}
                            hint="Downside risk adjusted"
                        />
                        <MetricCard
                            label="Recovery Factor"
                            value={Number.isFinite(analysis.recoveryFactor) ? fmtNum(analysis.recoveryFactor) : "∞"}
                            hint="Net P&L / Max DD"
                        />
                        <MetricCard
                            label="Max DD Duration"
                            value={fmtDuration(analysis.maxDrawdownDurationMs)}
                            hint="Longest stretch under water"
                        />
                        <MetricCard label="Avg Trade Duration" value={fmtDuration(analysis.avgTradeDuration)} />
                        <MetricCard label="Median Duration" value={fmtDuration(analysis.medianTradeDuration)} />
                        <MetricCard label="Longest Trade" value={fmtDuration(analysis.longestTrade)} />
                        <MetricCard label="Shortest Trade" value={fmtDuration(analysis.shortestTrade)} />
                        <MetricCard label="Avg Daily P&L" value={fmtPnl(analysis.avgDailyPnl)} />
                        <MetricCard label="Avg Monthly P&L" value={fmtPnl(analysis.avgMonthlyPnl)} />
                        <MetricCard
                            label="Expectancy"
                            value={fmtPnl(analysis.expectancy)}
                            hint="Avg P&L per trade"
                            tone={analysis.expectancy >= 0 ? "positive" : "negative"}
                        />
                        <MetricCard
                            label="Max Drawdown"
                            value={fmtPnl(analysis.maxDrawdown)}
                            hint={`${fmtNum(analysis.maxDrawdownPct)}% from peak`}
                            tone="negative"
                        />
                    </CardContent>
                </Card>
            </div>

            {/* Streak & Extremes */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Streak Analysis</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 gap-3">
                        <MetricCard
                            label="Max Consecutive Wins"
                            value={String(analysis.maxConsecutiveWins)}
                            tone="positive"
                        />
                        <MetricCard
                            label="Max Consecutive Losses"
                            value={String(analysis.maxConsecutiveLosses)}
                            tone="negative"
                        />
                        <MetricCard
                            label="Avg Win Streak"
                            value={fmtNum(analysis.avgConsecutiveWins)}
                            tone="positive"
                        />
                        <MetricCard
                            label="Avg Loss Streak"
                            value={fmtNum(analysis.avgConsecutiveLosses)}
                            tone="negative"
                        />
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Extremes</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 gap-3">
                        <MetricCard
                            label="Best Day"
                            value={fmtPnl(analysis.bestDay.pnl)}
                            hint={fmtDate(analysis.bestDay.date)}
                            tone="positive"
                        />
                        <MetricCard
                            label="Worst Day"
                            value={fmtPnl(analysis.worstDay.pnl)}
                            hint={fmtDate(analysis.worstDay.date)}
                            tone="negative"
                        />
                        <MetricCard
                            label="Best Month"
                            value={fmtPnl(analysis.bestMonth.pnl)}
                            hint={analysis.bestMonth.monthLabel}
                            tone="positive"
                        />
                        <MetricCard
                            label="Worst Month"
                            value={fmtPnl(analysis.worstMonth.pnl)}
                            hint={analysis.worstMonth.monthLabel}
                            tone="negative"
                        />
                        <MetricCard
                            label="Best Year"
                            value={fmtPnl(analysis.bestYear.pnl)}
                            hint={String(analysis.bestYear.year)}
                            tone="positive"
                        />
                        <MetricCard
                            label="Worst Year"
                            value={fmtPnl(analysis.worstYear.pnl)}
                            hint={String(analysis.worstYear.year)}
                            tone="negative"
                        />
                    </CardContent>
                </Card>
            </div>

            {/* Long vs Short */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Long vs Short</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                        <div className="rounded-lg border bg-emerald-500/5 p-3">
                            <p className="text-xs text-muted-foreground">Long Trades</p>
                            <p className="text-lg font-semibold tabular-nums">{analysis.longTrades.count}</p>
                            <p className="text-xs text-muted-foreground">
                                Win Rate: {analysis.longTrades.winRate.toFixed(1)}% · Avg: {fmtPnl(analysis.longTrades.avgPnl)}
                            </p>
                        </div>
                        <div className="rounded-lg border bg-emerald-500/5 p-3">
                            <p className="text-xs text-muted-foreground">Long P&L</p>
                            <p
                                className={cn(
                                    "text-lg font-semibold tabular-nums",
                                    analysis.longTrades.pnl >= 0 ? "text-emerald-600" : "text-red-600",
                                )}
                            >
                                {fmtPnl(analysis.longTrades.pnl)}
                            </p>
                        </div>
                        <div className="rounded-lg border bg-red-500/5 p-3">
                            <p className="text-xs text-muted-foreground">Short Trades</p>
                            <p className="text-lg font-semibold tabular-nums">{analysis.shortTrades.count}</p>
                            <p className="text-xs text-muted-foreground">
                                Win Rate: {analysis.shortTrades.winRate.toFixed(1)}% · Avg: {fmtPnl(analysis.shortTrades.avgPnl)}
                            </p>
                        </div>
                        <div className="rounded-lg border bg-red-500/5 p-3">
                            <p className="text-xs text-muted-foreground">Short P&L</p>
                            <p
                                className={cn(
                                    "text-lg font-semibold tabular-nums",
                                    analysis.shortTrades.pnl >= 0 ? "text-emerald-600" : "text-red-600",
                                )}
                            >
                                {fmtPnl(analysis.shortTrades.pnl)}
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Expectancy per trading day, combined and per strategy */}
            <DailyExpectancyCard analysis={analysis} files={files} />

            {/* How strategies' daily returns move together */}
            <CorrelationCard correlation={correlation} />

            <DistributionCharts analysis={analysis} />
        </div>
    );
}

// ─── Breakdown Tab ──────────────────────────────────────────────────────────

type PnlBucket = { pnl: number; trades: number; winRate: number };

/** P&L per bucket as bars, one per row of `data`, labelled by `labelKey`. */
function PnlBucketChart<T extends PnlBucket>({
    title,
    description,
    data,
    labelKey,
}: {
    title: string;
    description: string;
    data: T[];
    labelKey: keyof T & string;
}) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey={(d: T) => String(d[labelKey])}
                                tickLine={false}
                                axisLine={false}
                                interval={0}
                                angle={-45}
                                textAnchor="end"
                                height={60}
                                fontSize={10}
                            />
                            <YAxis
                                tickFormatter={(v: number) => fmtPnlShort(v)}
                                tickLine={false}
                                axisLine={false}
                                width={60}
                            />
                            <Tooltip
                                content={({ active, payload }) => {
                                    if (!active || !payload?.length) return null;
                                    const d = payload[0]!.payload as T;
                                    return (
                                        <div className="rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-sm">
                                            <p className="font-medium">{String(d[labelKey])}</p>
                                            <p className={d.pnl >= 0 ? "text-emerald-600" : "text-red-600"}>
                                                P&L: {fmtPnl(d.pnl)}
                                            </p>
                                            <p className="text-muted-foreground">
                                                Trades: {d.trades} · Win Rate: {d.winRate.toFixed(1)}%
                                            </p>
                                        </div>
                                    );
                                }}
                            />
                            <ReferenceLine y={0} stroke="#64748b" strokeDasharray="3 3" />
                            <Bar dataKey="pnl" name="P&L" radius={[2, 2, 0, 0]} maxBarSize={40}>
                                {data.map((entry, i) => (
                                    <Cell key={i} fill={entry.pnl >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
}

function BreakdownTab({ analysis }: { analysis: Analysis }) {
    return (
        <div className="flex flex-col gap-4">
            {/* Monthly P&L Chart */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Monthly P&L</CardTitle>
                    <CardDescription>Net profit/loss by month</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="h-64 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={analysis.monthlyPnl} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis
                                    dataKey="monthLabel"
                                    tickLine={false}
                                    axisLine={false}
                                    interval={0}
                                    angle={-45}
                                    textAnchor="end"
                                    height={60}
                                />
                                <YAxis
                                    tickFormatter={(v: number) => fmtPnlShort(v)}
                                    tickLine={false}
                                    axisLine={false}
                                    width={60}
                                />
                                <Tooltip
                                    content={({ active, payload }) => {
                                        if (!active || !payload?.length) return null;
                                        const d = payload[0]!.payload as MonthlyPnL;
                                        return (
                                            <div className="rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-sm">
                                                <p className="font-medium">{d.monthLabel}</p>
                                                <p className={d.pnl >= 0 ? "text-emerald-600" : "text-red-600"}>
                                                    P&L: {fmtPnl(d.pnl)}
                                                </p>
                                                <p className="text-muted-foreground">
                                                    Trades: {d.trades} · Win Rate: {d.winRate.toFixed(1)}%
                                                </p>
                                            </div>
                                        );
                                    }}
                                />
                                <ReferenceLine y={0} stroke="#64748b" strokeDasharray="3 3" />
                                <Bar dataKey="pnl" name="P&L" radius={[2, 2, 0, 0]} maxBarSize={40}>
                                    {analysis.monthlyPnl.map((entry, i) => (
                                        <Cell key={i} fill={entry.pnl >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Yearly P&L Chart */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Yearly P&L</CardTitle>
                    <CardDescription>Annual performance summary</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="h-52 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={analysis.yearlyPnl} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="year" tickLine={false} axisLine={false} />
                                <YAxis
                                    tickFormatter={(v: number) => fmtPnlShort(v)}
                                    tickLine={false}
                                    axisLine={false}
                                    width={60}
                                />
                                <Tooltip
                                    content={({ active, payload }) => {
                                        if (!active || !payload?.length) return null;
                                        const d = payload[0]!.payload as YearlyPnL;
                                        return (
                                            <div className="rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-sm">
                                                <p className="font-medium">{d.year}</p>
                                                <p className={d.pnl >= 0 ? "text-emerald-600" : "text-red-600"}>
                                                    P&L: {fmtPnl(d.pnl)}
                                                </p>
                                                <p className="text-muted-foreground">
                                                    Trades: {d.trades} · Win Rate: {d.winRate.toFixed(1)}% · Avg:{" "}
                                                    {fmtPnl(d.avgPnl)}
                                                </p>
                                            </div>
                                        );
                                    }}
                                />
                                <ReferenceLine y={0} stroke="#64748b" strokeDasharray="3 3" />
                                <Bar dataKey="pnl" name="P&L" radius={[4, 4, 0, 0]} maxBarSize={64}>
                                    {analysis.yearlyPnl.map((entry, i) => (
                                        <Cell key={i} fill={entry.pnl >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Entry Window P&L Chart */}
            <PnlBucketChart
                title="Entry Time P&L"
                description="Net profit/loss by the 15 minute window the trade was entered in"
                data={analysis.entryWindowPnl}
                labelKey="window"
            />

            {/* Exit Window P&L Chart */}
            <PnlBucketChart
                title="Exit Time P&L"
                description="Net profit/loss by the 15 minute window the trade was exited in"
                data={analysis.exitWindowPnl}
                labelKey="window"
            />

            {/* Weekday P&L Chart */}
            <PnlBucketChart
                title="Day of Week P&L"
                description="Net profit/loss by weekday, Sunday to Saturday"
                data={analysis.weekdayPnl}
                labelKey="day"
            />

            {/* Monthly P&L Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Monthly P&L Table</CardTitle>
                    <CardDescription>Net P&L by exit month, with each year's return ÷ max drawdown</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs tabular-nums">
                            <thead>
                                <tr className="border-b text-right text-muted-foreground">
                                    <th className="pb-2 pr-3 text-left font-medium">Year</th>
                                    {MONTH_LABELS.map((label) => (
                                        <th key={label} className="pb-2 px-2 font-medium">
                                            {label}
                                        </th>
                                    ))}
                                    <th className="pb-2 pl-3 font-medium" title="Year's net P&L ÷ that year's max drawdown">
                                        Return / Max DD
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {analysis.monthGrid.map((row) => (
                                    <tr key={row.year} className="border-b last:border-0">
                                        <td className="py-1.5 pr-3 font-medium">{row.year}</td>
                                        {row.months.map((pnl, i) => (
                                            <td
                                                key={i}
                                                className={cn(
                                                    "px-2 py-1.5 text-right",
                                                    pnl === null
                                                        ? "text-muted-foreground"
                                                        : pnl >= 0
                                                          ? "text-emerald-600"
                                                          : "text-red-600",
                                                )}
                                            >
                                                {pnl === null ? "-" : fmtPnl(pnl)}
                                            </td>
                                        ))}
                                        <td
                                            className={cn(
                                                "py-1.5 pl-3 text-right font-medium",
                                                row.pnl >= 0 ? "text-emerald-600" : "text-red-600",
                                            )}
                                        >
                                            {row.maxDrawdown > 0 ? fmtNum(row.pnl / row.maxDrawdown, 2) : "-"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            {/* Symbol Breakdown */}
            {analysis.symbolStats.length > 1 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Per-Symbol Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b text-left text-xs text-muted-foreground">
                                        <th className="pb-2 pr-4 font-medium">Symbol</th>
                                        <th className="pb-2 pr-4 text-right font-medium">Trades</th>
                                        <th className="pb-2 pr-4 text-right font-medium">Win Rate</th>
                                        <th className="pb-2 pr-4 text-right font-medium">Avg P&L</th>
                                        <th className="pb-2 pr-4 text-right font-medium">Best</th>
                                        <th className="pb-2 text-right font-medium">Total P&L</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {analysis.symbolStats.map((s) => (
                                        <tr key={s.symbol} className="border-b last:border-0">
                                            <td className="py-1.5 pr-4 font-medium">{s.symbol}</td>
                                            <td className="py-1.5 pr-4 text-right tabular-nums">{s.trades}</td>
                                            <td className="py-1.5 pr-4 text-right tabular-nums">{s.winRate.toFixed(1)}%</td>
                                            <td className="py-1.5 pr-4 text-right tabular-nums">{fmtPnl(s.avgPnl)}</td>
                                            <td
                                                className={cn(
                                                    "py-1.5 pr-4 text-right tabular-nums",
                                                    s.bestTrade >= 0 ? "text-emerald-600" : "text-red-600",
                                                )}
                                            >
                                                {fmtPnl(s.bestTrade)}
                                            </td>
                                            <td
                                                className={cn(
                                                    "py-1.5 text-right font-medium tabular-nums",
                                                    s.pnl >= 0 ? "text-emerald-600" : "text-red-600",
                                                )}
                                            >
                                                {fmtPnl(s.pnl)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

// ─── Distribution (end of Overview) ─────────────────────────────────────────

function DistributionCharts({ analysis }: { analysis: Analysis }) {
    const pieData = [
        { name: "Wins", value: analysis.winningTrades, fill: POSITIVE_COLOR },
        { name: "Losses", value: analysis.losingTrades, fill: NEGATIVE_COLOR },
        { name: "Breakeven", value: analysis.breakevenTrades, fill: NEUTRAL_COLOR },
    ].filter((d) => d.value > 0);

    return (
        <>
            {/* P&L Distribution */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">P&L Distribution</CardTitle>
                    <CardDescription>
                        Distribution of trade P&L values · {analysis.totalTrades} trades
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="h-64 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={analysis.pnlDistribution} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis
                                    dataKey="range"
                                    tickLine={false}
                                    axisLine={false}
                                    interval={0}
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                    fontSize={10}
                                />
                                <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={32} />
                                <Tooltip
                                    content={({ active, payload }) => {
                                        if (!active || !payload?.length) return null;
                                        const d = payload[0]!.payload as HistogramBin;
                                        return (
                                            <div className="rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-sm">
                                                <p className="font-medium">{d.range}</p>
                                                <p className="text-muted-foreground">{d.count} trades</p>
                                            </div>
                                        );
                                    }}
                                    cursor={{ fill: "rgba(0,0,0,0.05)" }}
                                />
                                <Bar dataKey="count" name="Trades" radius={[2, 2, 0, 0]} maxBarSize={48}>
                                    {analysis.pnlDistribution.map((bin, i) => (
                                        <Cell key={i} fill={bin.fill} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Win/Loss Pie */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Win/Loss Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-center">
                            <div className="h-56 w-56">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={80}
                                            paddingAngle={2}
                                            dataKey="value"
                                        >
                                            {pieData.map((entry, i) => (
                                                <Cell key={i} fill={entry.fill} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            content={({ active, payload }) => {
                                                if (!active || !payload?.length) return null;
                                                const d = payload[0]!;
                                                return (
                                                    <div className="rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-sm">
                                                        <p className="font-medium" style={{ color: (d.payload as { fill: string }).fill }}>
                                                            {d.name}: {d.value} trades
                                                        </p>
                                                    </div>
                                                );
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div className="mt-2 flex justify-center gap-4 text-xs">
                            {pieData.map((d) => (
                                <div key={d.name} className="flex items-center gap-1.5">
                                    <span className="inline-block size-2.5 rounded-full" style={{ backgroundColor: d.fill }} />
                                    <span className="text-muted-foreground">
                                        {d.name}: {d.value} ({((d.value / analysis.totalTrades) * 100).toFixed(1)}%)
                                    </span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Daily P&L Chart */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Daily P&L</CardTitle>
                        <CardDescription>P&L per trading day</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={analysis.dailyPnl} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis
                                        dataKey="date"
                                        tickFormatter={(v: string) => {
                                            return fmtDayTick(v);
                                        }}
                                        tickLine={false}
                                        axisLine={false}
                                        interval="preserveStartEnd"
                                        minTickGap={40}
                                    />
                                    <YAxis
                                        tickFormatter={(v: number) => fmtPnlShort(v)}
                                        tickLine={false}
                                        axisLine={false}
                                        width={50}
                                    />
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (!active || !payload?.length) return null;
                                            const d = payload[0]!.payload as { date: string; pnl: number };
                                            return (
                                                <div className="rounded-lg border bg-background px-2.5 py-1.5 text-xs shadow-sm">
                                                    <p className="font-medium">{fmtDate(d.date)}</p>
                                                    <p className={d.pnl >= 0 ? "text-emerald-600" : "text-red-600"}>
                                                        {fmtPnl(d.pnl)}
                                                    </p>
                                                </div>
                                            );
                                        }}
                                    />
                                    <ReferenceLine y={0} stroke="#64748b" strokeDasharray="3 3" />
                                    <Bar dataKey="pnl" name="P&L" radius={[1, 1, 0, 0]} maxBarSize={20}>
                                        {analysis.dailyPnl.map((entry, i) => (
                                            <Cell key={i} fill={entry.pnl >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}

// ─── Trades Tab ─────────────────────────────────────────────────────────────

function TradesTab({
    analysis,
    filters,
    onFiltersChange,
    sort,
    onSortChange,
    showCosts,
    onShowCostsChange,
}: {
    analysis: Analysis;
    filters: TradeFilters;
    onFiltersChange: (next: TradeFilters) => void;
    sort: TradeSort;
    onSortChange: (next: TradeSort) => void;
    showCosts: boolean;
    onShowCostsChange: (next: boolean) => void;
}) {
    const visible = useMemo(
        () => sortTrades(filterTrades(analysis.trades, filters), sort),
        [analysis.trades, filters, sort],
    );
    /** Clicking the sorted column flips its direction; a new column starts from its largest values. */
    const sortBy = (key: TradeSortKey) =>
        onSortChange({ key, dir: sort.key === key && sort.dir === "desc" ? "asc" : "desc" });
    const sortHeader = (key: TradeSortKey, label: string) => (
        <th className="pb-2 pr-3 text-right font-medium">
            <button
                type="button"
                onClick={() => sortBy(key)}
                className={cn(
                    "inline-flex items-center gap-1 transition-colors hover:text-foreground",
                    sort.key === key && "text-foreground",
                )}
            >
                {label}
                <span className="text-[0.65rem]">{sort.key === key ? (sort.dir === "asc" ? "▲" : "▼") : ""}</span>
            </button>
        </th>
    );
    const costCell = (value: number) => (
        <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
            {value > 0 ? fmtPnl(value) : "-"}
        </td>
    );
    const columnCount = showCosts ? 13 : 10;

    return (
        <div className="flex flex-col gap-4">
            <TradeFilterBar
                filters={filters}
                onChange={onFiltersChange}
                matched={visible.length}
                total={analysis.trades.length}
            />

            <Card>
                <CardHeader>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <CardTitle className="text-base">All Trades</CardTitle>
                            <CardDescription>
                                {analysis.totalTrades} trades in chronological order
                                {analysis.openTrades > 0 && ` · ${analysis.openTrades} still open (excluded from stats)`}
                            </CardDescription>
                        </div>
                        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                            <input
                                type="checkbox"
                                className="size-3.5 accent-primary"
                                checked={showCosts}
                                onChange={(e) => onShowCostsChange(e.target.checked)}
                            />
                            Show fees, brokerage &amp; slippage
                        </label>
                    </div>
                </CardHeader>
                <CardContent>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b text-left text-xs text-muted-foreground">
                                <th className="pb-2 pr-3 font-medium">#</th>
                                <th className="pb-2 pr-3 font-medium">Symbol</th>
                                <th className="pb-2 pr-3 font-medium">Side</th>
                                <th className="pb-2 pr-3 text-right font-medium">Qty</th>
                                <th className="pb-2 pr-3 text-right font-medium">Entry</th>
                                <th className="pb-2 pr-3 text-right font-medium">Exit</th>
                                {sortHeader("entryTime", "Entry Time")}
                                {sortHeader("exitTime", "Exit Time")}
                                {sortHeader("pnlPct", "P&L %")}
                                {showCosts && <th className="pb-2 pr-3 text-right font-medium">Fees</th>}
                                {showCosts && <th className="pb-2 pr-3 text-right font-medium">Brokerage</th>}
                                {showCosts && <th className="pb-2 pr-3 text-right font-medium">Slippage</th>}
                                {sortHeader("pnl", "Net P&L")}
                            </tr>
                        </thead>
                        <tbody>
                            {visible.length === 0 && (
                                <tr>
                                    <td colSpan={columnCount} className="py-6 text-center text-sm text-muted-foreground">
                                        No trades match the current filters.
                                    </td>
                                </tr>
                            )}
                            {visible.map((t, i) => (
                                <tr key={i} className="border-b last:border-0 hover:bg-muted/50">
                                    <td className="py-1.5 pr-3 tabular-nums text-muted-foreground">{i + 1}</td>
                                    <td className="py-1.5 pr-3 font-medium">{t.symbol}</td>
                                    <td className="py-1.5 pr-3">
                                        <Badge
                                            variant={t.side === "LONG" ? "default" : "secondary"}
                                            className="text-[0.65rem]"
                                        >
                                            {t.side}
                                        </Badge>
                                    </td>
                                    <td className="py-1.5 pr-3 text-right tabular-nums">{t.quantity}</td>
                                    <td className="py-1.5 pr-3 text-right tabular-nums">{fmtPnl(t.entryPrice)}</td>
                                    <td className="py-1.5 pr-3 text-right tabular-nums">
                                        {t.isOpen ? "-" : fmtPnl(t.exitPrice)}
                                    </td>
                                    <td className="py-1.5 pr-3 text-right tabular-nums text-xs">{fmtDateTime(t.entryDate)}</td>
                                    <td className="py-1.5 pr-3 text-right tabular-nums text-xs">
                                        {t.isOpen ? "open" : fmtDateTime(t.exitDate)}
                                    </td>
                                    <td
                                        className={cn(
                                            "py-1.5 pr-3 text-right tabular-nums",
                                            t.isOpen
                                                ? "text-muted-foreground"
                                                : t.pnlPct >= 0
                                                  ? "text-emerald-600"
                                                  : "text-red-600",
                                        )}
                                    >
                                        {t.isOpen ? "-" : fmtPct(t.pnlPct)}
                                    </td>
                                    {showCosts && costCell(t.fees)}
                                    {showCosts && costCell(t.brokerage)}
                                    {showCosts && costCell(t.slippage)}
                                    <td
                                        className={cn(
                                            "py-1.5 text-right font-medium tabular-nums",
                                            t.isOpen
                                                ? "text-muted-foreground"
                                                : t.netPnl >= 0
                                                  ? "text-emerald-600"
                                                  : "text-red-600",
                                        )}
                                    >
                                        {t.isOpen ? "-" : fmtPnl(t.netPnl)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                </CardContent>
            </Card>
        </div>
    );
}
